import React from 'react';
import { FileText, MessageSquare, Languages, Image, Video, Shield, Library, Brain } from 'lucide-react';
import FadeIn from './FadeIn';

const Features = () => {
  const features = [
    {
      icon: FileText,
      title: '100+ Šablon',
      description: 'Přípravy, testy, pracovní listy, IVP a další – vše pro MŠ, ZŠ i SŠ',
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: MessageSquare,
      title: 'AI Chat',
      description: 'Konverzace s AI asistentem pro rychlé vytváření materiálů',
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Brain,
      title: 'AI Asistenti',
      description: 'AI asistenti pracují výhradně s vašimi školními dokumenty',
      iconColor: 'text-violet-600',
      bgColor: 'bg-violet-50'
    },
    {
      icon: Languages,
      title: 'Překlady do 50+ jazyků',
      description: 'Překladový nástroj s pedagogickou terminologií',
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: Image,
      title: 'Generování obrázků',
      description: 'Ilustrace, diagramy a fotorealistické obrázky pro výuku',
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: Video,
      title: 'Inteligentní shrnutí videí',
      description: 'Vložte odkaz na video a Školobot vytáhne to nejdůležitější',
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      icon: Shield,
      title: 'AI Detektor',
      description: 'Zjistěte, jak moc se na práci žáků podílela AI - analýza 0-100%',
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      icon: Library,
      title: 'Knihovna & Historie',
      description: 'Správa všech materiálů a vlastní struktura složek',
      iconColor: 'text-teal-600',
      bgColor: 'bg-teal-50'
    }
  ];

  return (
    <section 
      id="features" 
      className="py-24 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[color:var(--text-primary)] mb-4">
              Váš kompletní pedagogický AI arzenál
            </h2>
            <p className="text-lg text-[color:var(--text-secondary)]">
              Vše, co potřebujete k moderní výuce a administrativě
            </p>
          </div>
        </FadeIn>

        <div className="space-y-6">
          {/* Row 1: 2 items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.slice(0, 2).map((feature, index) => {
              const Icon = feature.icon;
              return (
                <FadeIn key={index} delay={0}>
                  <div className={`${feature.bgColor} rounded-2xl p-8 border border-white hover:shadow-xl transition-all`}>
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon className={`w-8 h-8 ${feature.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[color:var(--text-primary)] mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-base text-[color:var(--text-secondary)]">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          {/* Row 2: 4 items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.slice(2, 6).map((feature, index) => {
              const Icon = feature.icon;
              return (
                <FadeIn key={index + 2} delay={0}>
                  <div className={`${feature.bgColor} rounded-2xl p-6 border border-white hover:shadow-xl transition-all text-center h-full`}>
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-lg font-bold text-[color:var(--text-primary)] mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-[color:var(--text-secondary)]">
                      {feature.description}
                    </p>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          {/* Row 3: 2 items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.slice(6, 8).map((feature, index) => {
              const Icon = feature.icon;
              return (
                <FadeIn key={index + 6} delay={0}>
                  <div className={`${feature.bgColor} rounded-2xl p-8 border border-white hover:shadow-xl transition-all`}>
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon className={`w-8 h-8 ${feature.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[color:var(--text-primary)] mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-base text-[color:var(--text-secondary)]">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;